import { Controller } from "@hotwired/stimulus"
import { GROSS_PER_RATE, NET_PER_RATE } from "../../utils/calculation.util"
import { getUserLocale, parseInputNumber } from "../../utils/number-format.util"

const MODES = [NET_PER_RATE, GROSS_PER_RATE]

// Fired by documents--vat-payer-toggle once it has shown or hidden the VAT columns, with the
// supplier's status (detail.isVatPayer)
const VAT_PAYER_CHANGE = "documents--vat-payer-toggle:change"

const FORM_EVENTS = ["input", "change", "click", VAT_PAYER_CHANGE]

const TYPE_SELECT = 'select[name="expense[expense_type]"]'
const DOCUMENT_AMOUNT = '[name="expense[discount_amount]"]'
const LINE_AMOUNT = '[name$="[discount_unit_price]"]'
const LINE_AMOUNT_NAME = /\[discount_unit_price\]$/
const DESTROY_NAME = /\[_destroy\]$/

// Connects to data-controller="expenses--vat-calculation-mode"
//
// The "Výpočet DPH" switch of the expense form: zo základu (net_per_rate) or zo sumy s DPH
// (gross_per_rate). It hands the choice to the totals calculator, which names the method in the
// summary, moves the document discount and every line to it (the hidden gross posted in gross mode
// only) and totals in it.
//
// Two rules keep the switch honest:
//  - A NEW expense follows its type until the user picks a mode (receipts are printed with the VAT
//    inside the price, spec §7.1 / D-1) - but only while its supplier is a VAT payer. A supplier who
//    is not has no VAT to calculate: the VAT columns, and this switch with them, are hidden, and the
//    mode is net_per_rate. Showing the columns again brings the type's default back. A saved
//    expense never moves on its own, whoever the supplier is: a stored gross stays gross, and so
//    does this switch stay shown while the mode is gross (#syncVisibility), the way out of the EUR
//    discount toggles that gross locks.
//  - gross_per_rate takes percentage discounts only (spec NG5), so the gross segment is disabled,
//    with a hint, while the net one is selected and a fixed-amount discount is set. The selected
//    segment is never the one disabled: a disabled radio is not posted, and the mode must be.
//
// A saved expense whose importer chose its mode carries a "podľa dokladu" label (the sourceLabel
// target). It is true only of the mode the importer chose, so the user's first change hides it
// (#choose). The moves this controller makes itself (the type's default, the supplier's VAT
// status) go through #apply and leave it alone: they are not the user's choice.
export default class extends Controller {
  static outlets = ["documents--totals-calculator"]

  static targets = ["radio", "radioGroup", "grossHint", "sourceLabel"]

  static values = {
    newRecord: Boolean,
    userChosen: Boolean,
    // { expense_type => mode }, for every type (Expense.default_vat_calculation_mode_for)
    defaults: Object,
    // Whether the supplier is a VAT payer: as the server rendered the form, then as
    // documents--vat-payer-toggle last said. A blank one counts as a payer, so does a form without the toggle.
    vatPayer: { type: Boolean, default: true }
  }

  // What the switch listens to: the form it sits in (the page, outside one).
  #form = null
  #onFormEvent = null

  // The gross segment and its label, found once: they are part of this controller's own markup.
  #grossRadio = null
  #grossLabel = null

  // The frame that will bring the gross segment up to date (#scheduleGrossSegmentSync), if one is waiting.
  #frame = null

  connect() {
    // The type select, the discount fields and the lines are all elsewhere in the form. One
    // listener on the form sees them (and a select or a line that is replaced later); `click`
    // is there for the controls that clear a discount without an input event.
    this.#form = this.element.closest("form") || document
    this.#onFormEvent = (event) => this.#handleFormEvent(event)
    for (const name of FORM_EVENTS) this.#form.addEventListener(name, this.#onFormEvent)

    this.#grossRadio = this.radioTargets.find((radio) => radio.value === GROSS_PER_RATE) || null
    this.#grossLabel = this.#grossRadio
      ? Array.from(this.element.querySelectorAll("label")).find((label) => label.htmlFor === this.#grossRadio.id)
      : null

    this.#syncGrossSegment()
    this.#syncVisibility()
  }

  disconnect() {
    for (const name of FORM_EVENTS) this.#form?.removeEventListener(name, this.#onFormEvent)
    if (this.#frame !== null) cancelAnimationFrame(this.#frame)
    this.#form = null
    this.#onFormEvent = null
    this.#grossRadio = null
    this.#grossLabel = null
    this.#frame = null
  }

  // A radio changed: the user picked a mode.
  choose(event) {
    const mode = event.target.value
    if (!MODES.includes(mode)) return

    this.userChosenValue = true
    this.#hideSourceLabel()
    this.#apply(mode)
  }

  // The help popover's shown / hidden events (Bootstrap fires them on the trigger).
  helpShown(event) {
    event.currentTarget.setAttribute("aria-expanded", "true")
  }

  helpHidden(event) {
    event.currentTarget.setAttribute("aria-expanded", "false")
  }

  #handleFormEvent(event) {
    if (event.type === "change" && event.target.matches?.(TYPE_SELECT)) this.#follow(event.target.value)
    if (event.type === VAT_PAYER_CHANGE) {
      this.vatPayerValue = event.detail.isVatPayer
      this.#follow(this.#form.querySelector(TYPE_SELECT)?.value)
      this.#syncVisibility()
    }

    this.#scheduleGrossSegmentSync()
  }

  // The mode a NEW expense that no one has chosen for yet starts in: its type's default while the
  // supplier is a VAT payer, net_per_rate while it is not (the VAT columns, and this switch, hidden).
  #follow(type) {
    if (!this.newRecordValue || this.userChosenValue) return

    const mode = this.vatPayerValue ? this.defaultsValue[type] : NET_PER_RATE
    if (!MODES.includes(mode)) return

    const radio = this.radioTargets.find((candidate) => candidate.value === mode)
    if (!radio) return

    // Whether a segment may be moved to depends on the discounts as they are now, not as of the last frame.
    this.#flushGrossSegmentSync()
    if (radio.disabled || radio.checked) return

    radio.checked = true
    this.#apply(mode)
  }

  // Shown with the VAT columns, and whoever the supplier is while the mode is gross: gross locks the
  // EUR discount toggles, and this is the way out. Not a [data-vat-column], so that the toggle, which
  // hides those on every change and every row added, leaves it to this. The server renders the same.
  //
  // Worked out as it renders (connect) and when the supplier's VAT status changes, never on a pick:
  // hiding the switch under the user's own pick of net took the focus, and the way back to gross,
  // with it. A non-payer's expense switched to net keeps its switch until the next of those.
  #syncVisibility() {
    this.element.classList.toggle("d-none", !this.vatPayerValue && !this.#grossRadio?.checked)
  }

  // The label is the radio group's description (aria-describedby), and a hidden element that is
  // named there is still read out, so the description goes with it.
  #hideSourceLabel() {
    if (!this.hasSourceLabelTarget) return

    this.sourceLabelTarget.classList.add("d-none")

    if (!this.hasRadioGroupTarget) return
    if (this.radioGroupTarget.getAttribute("aria-describedby") === this.sourceLabelTarget.id) {
      this.radioGroupTarget.removeAttribute("aria-describedby")
    }
  }

  #apply(mode) {
    if (this.hasDocumentsTotalsCalculatorOutlet) this.documentsTotalsCalculatorOutlet.vatModeValue = mode

    this.dispatch("change", { detail: { mode } })
    this.#syncGrossSegment()
  }

  // Every input, click and change in the form comes here, a keystroke in any line among them, and
  // the segment is read off the whole form: once a frame is enough.
  #scheduleGrossSegmentSync() {
    if (this.#frame !== null) return

    this.#frame = requestAnimationFrame(() => {
      this.#frame = null
      this.#syncGrossSegment()
    })
  }

  // The sync that is waiting for its frame, run now.
  #flushGrossSegmentSync() {
    if (this.#frame === null) return

    cancelAnimationFrame(this.#frame)
    this.#frame = null
    this.#syncGrossSegment()
  }

  #syncGrossSegment() {
    const gross = this.#grossRadio
    if (!gross) return

    const disabled = !gross.checked && this.#amountDiscountSet()
    gross.disabled = disabled

    this.#grossLabel?.classList.toggle("radio-group__label--disabled", disabled)

    if (!this.hasGrossHintTarget) return

    this.grossHintTarget.classList.toggle("d-none", !disabled)
    if (disabled && this.grossHintTarget.id) {
      gross.setAttribute("aria-describedby", this.grossHintTarget.id)
    } else {
      gross.removeAttribute("aria-describedby")
    }
  }

  // A fixed-amount discount on the document or on a line that is still on the form. A discount
  // controller that clears its field sets the value without an event; the next click, input or
  // change in the form brings it here.
  #amountDiscountSet() {
    const locale = getUserLocale()
    const positive = (input) => (parseInputNumber(input, locale) || 0) > 0

    const documentAmount = this.#form.querySelector(DOCUMENT_AMOUNT)
    if (documentAmount && positive(documentAmount)) return true

    const lineAmounts = Array.from(this.#form.querySelectorAll(LINE_AMOUNT)).filter(positive)
    if (lineAmounts.length === 0) return false

    // Only now, and once for all the lines: the names of the lines marked for destruction.
    const removed = this.#removedLines()
    return lineAmounts.some((input) => !removed.has(input.name.replace(LINE_AMOUNT_NAME, "")))
  }

  // A removed line keeps its fields on the form, marked for destruction. Its fields share a
  // name prefix (expense[expense_items_attributes][3]); this is the set of those of removed lines.
  #removedLines() {
    const removed = new Set()
    for (const input of this.#form.querySelectorAll('input[name$="[_destroy]"]')) {
      if (["true", "1"].includes(input.value)) removed.add(input.name.replace(DESTROY_NAME, ""))
    }
    return removed
  }
}
